import { useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import {
  AlertBanner,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@lendcord/concord";
import { isQueryApiError } from "@/app/store/baseApi";
import { useDeleteBranchMutation } from "../api/branches.api";
import type { Branch } from "../setup.types";

interface DeleteBranchDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  branch: Branch;
}

// Deleting a branch with staff/agents/borrowers/lenders still linked to it
// (users.branch_id) is a 409 (LEND-105) from lendcord-core, not a client-side
// guard — the backend's error message already explains the fix ("reassign
// them before deleting"), so it's surfaced as-is rather than re-worded here.
export function DeleteBranchDialog({ open, onOpenChange, branch }: DeleteBranchDialogProps) {
  const intl = useIntl();
  const [deleteBranch, { isLoading }] = useDeleteBranchMutation();
  const [error, setError] = useState<string | undefined>();

  useEffect(() => {
    if (open) setError(undefined);
  }, [open]);

  async function handleDelete() {
    setError(undefined);
    try {
      await deleteBranch(branch.id).unwrap();
      onOpenChange(false);
    } catch (err) {
      setError(isQueryApiError(err) ? err.message : intl.formatMessage({ id: "lender.branches.deleteError" }));
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            <FormattedMessage id="lender.branches.deleteDialogTitle" />
          </DialogTitle>
          <DialogDescription>
            <FormattedMessage id="lender.branches.deleteDialogDescription" values={{ name: branch.name }} />
          </DialogDescription>
        </DialogHeader>

        {error && <AlertBanner status="error">{error}</AlertBanner>}

        <DialogFooter>
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} disabled={isLoading}>
            <FormattedMessage id="lender.shared.cancelButton" />
          </Button>
          <Button type="button" variant="destructive" onClick={handleDelete} disabled={isLoading}>
            {isLoading ? (
              <FormattedMessage id="lender.branches.deletingButton" />
            ) : (
              <FormattedMessage id="lender.branches.deleteButton" />
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
